import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { Sparkles, Download, RotateCcw, Plus, Info, AlertTriangle, Upload, Save, Database } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

import {
  ACTUALS, ACTUAL_MONTHS, ALL_MONTHS, FUTURE_MONTHS, CHANNELS, CATEGORY_OPTIONS,
} from '@/lib/smart-projections-data';
import {
  fmt, val, monthIndex,
  smartCalc, calculateFromParams,
  normalizeAllWeights, roundWeightsToWhole, redistributeAfterEdit,
  activeWeightTotal, weightsValid,
  isNewOrSparseSku, eligiblePeers, skuCategory, extractWeight,
  registerActualMonth, computeChannelSplit, predictSparseSku, isComboSku,
} from '@/lib/smart-projections-engine';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('radar_session_token') || ''}`,
});

// Persistent storage keys — MUST match the source HTML so prior user
// edits (added SKUs, edited projections) survive migration into RADAR.
const NEW_SKU_STORAGE_KEY = 'RADAR_NEW_SKUS_V1';
const EDITED_PROJECTIONS_KEY = 'RADAR_EDITED_PROJECTIONS_V1';
const editedKey = (sku, month) => `${sku}|||${month}`;

/**
 * Smart Projections page — thin React shell around the source-of-truth
 * projection engine. All calculations delegate to smart-projections-engine.js
 * so numbers match the audited HTML export exactly.
 */
export default function SmartProjectionsPage() {
  // "Actuals" is the live source. When the user adds a new (unfed) SKU we
  // register it with an empty history so it flows into peer discovery + selects.
  const [actualsRegistry, setActualsRegistry] = useState(() => ({ ...ACTUALS }));
  // Full per-channel breakdown of every fed month:
  //   { [sku]: { [month]: { total: <units>, channels: { [name]: units } } } }
  // Powers the dynamic Channel Split derivation.
  const [channelActuals, setChannelActuals] = useState({});
  const [newSkus, setNewSkus] = useState({});
  const [editedProjections, setEditedProjections] = useState({});

  // Default target = first future month (matches source HTML behaviour).
  const [target, setTarget] = useState(FUTURE_MONTHS[0] || ALL_MONTHS[ALL_MONTHS.length - 1]);
  const [sku, setSku] = useState('');
  const [params, setParams] = useState([]);
  const [channelPct, setChannelPct] = useState(CHANNELS.map((c) => c[1]));
  // Tracks whether the user has manually edited a channel percentage. Once
  // touched we stop overwriting from history so their override persists.
  const [channelPctManuallyEdited, setChannelPctManuallyEdited] = useState(false);
  const [weightMode, setWeightMode] = useState('auto'); // 'auto' | 'manual'
  const [finalValue, setFinalValue] = useState(0);
  const [manualFinal, setManualFinal] = useState(false);

  // Add-New-SKU form
  const [newSkuName, setNewSkuName] = useState('');
  const [newSkuCategory, setNewSkuCategory] = useState('creatine');
  const [newSkuWeight, setNewSkuWeight] = useState('');

  // Combo/bundle SKUs (names containing `+`, `×` or `xN`) are hidden from
  // every Smart Projections view + calculation. Underlying `actualsRegistry`
  // is preserved so other RADAR tabs remain unaffected.
  const skuList = useMemo(
    () => Object.keys(actualsRegistry).filter((s) => !isComboSku(s)).sort(),
    [actualsRegistry],
  );
  // Only offer months that make sense to project (month #1 onwards — matches source).
  const monthOptions = useMemo(() => ALL_MONTHS.slice(1), []);
  const prevSkuTargetRef = useRef({ sku: '', target: '' });

  // ---- Brand filter + multi-SKU selection ---------------------------------
  // Brand is derived via `normalizeCategory` (same rule as peer discovery)
  // — no schema change needed. `selectedSkus` powers the consolidated
  // multi-SKU table; the single `sku` remains the "focused" SKU for the
  // detail cards / parameter editor.
  const [brandFilter, setBrandFilter] = useState('all');
  const [selectedSkus, setSelectedSkus] = useState([]);
  const brandOptions = useMemo(() => {
    const brands = new Set();
    skuList.forEach((s) => { const b = skuCategory(s, newSkus); if (b) brands.add(b); });
    return ['all', ...Array.from(brands).sort()];
  }, [skuList, newSkus]);
  const visibleSkuList = useMemo(() => {
    if (brandFilter === 'all') return skuList;
    return skuList.filter((s) => skuCategory(s, newSkus) === brandFilter);
  }, [skuList, newSkus, brandFilter]);

  // ---- Per-SKU edit cache — preserves manual work when navigating ----------
  // Every time the focused SKU changes we snapshot the current edits into
  // this ref keyed by the *outgoing* SKU, then restore any prior snapshot
  // for the *incoming* SKU. Cleared only by Reset or a full page reload.
  const perSkuStateRef = useRef({});

  // ---- Three-axis mode selector (Smart / Manual / Mixed) -------------------
  // Semantics:
  //   • Smart  — engine outputs only; user edits are preserved in state but
  //              ignored while rendering / exporting.
  //   • Manual — user edits win outright; engine outputs shown as reference.
  //   • Mixed  — engine baseline, user override wins where present (default).
  const [modes, setModes] = useState({
    projection: 'mixed', params: 'mixed', channels: 'mixed',
  });

  // -------------------- Bootstrap: load persisted state ---------------------
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(NEW_SKU_STORAGE_KEY) || '{}');
      setNewSkus(stored);
      // Register the SKUs with empty history so they show in dropdowns / peers.
      setActualsRegistry((prev) => {
        const next = { ...prev };
        Object.keys(stored).forEach((s) => { if (!next[s]) next[s] = {}; });
        return next;
      });
    } catch { /* ignore corrupt JSON */ }
    try {
      setEditedProjections(JSON.parse(localStorage.getItem(EDITED_PROJECTIONS_KEY) || '{}'));
    } catch { /* ignore */ }
    // Pull server-persisted actuals overrides and merge into the engine's
    // registry. This is what powers the Data Feeding Center — actuals fed
    // from the UI immediately flow through smartCalc / peer / backtest.
    (async () => {
      try {
        const res = await axios.get(`${API}/smart-projections/overrides`, { headers: authHeaders() });
        applyOverrides(res.data?.overrides || {});
      } catch (e) {
        // Non-fatal — the page still works on bundled data only.
        console.warn('[smart-projections] override fetch failed:', e?.message);
      }
    })();
  }, []);

  // Merge server overrides into actualsRegistry + channelActuals + register any newly-actual months.
  // The overrides payload shape is:
  //   { [sku]: { [month]: { units, channels?: {name: units} } } }
  // Bundled ACTUALS only carries totals — channelActuals is a separate map so
  // we don't rewrite the source-of-truth engine data.
  const applyOverrides = (overrides) => {
    setActualsRegistry((prev) => {
      const next = { ...prev };
      Object.entries(overrides).forEach(([s, months]) => {
        const totals = {};
        Object.entries(months).forEach(([m, entry]) => {
          totals[m] = Number(entry?.units) || 0;
          registerActualMonth(m);
        });
        next[s] = { ...(next[s] || {}), ...totals };
      });
      return next;
    });
    setChannelActuals(() => {
      const next = {};
      Object.entries(overrides).forEach(([s, months]) => {
        const perMonth = {};
        Object.entries(months).forEach(([m, entry]) => {
          if (entry?.channels && Object.keys(entry.channels).length > 0) {
            perMonth[m] = {
              total: Number(entry.units) || 0,
              channels: Object.fromEntries(
                Object.entries(entry.channels).map(([k, v]) => [k, Number(v) || 0])
              ),
            };
          }
        });
        if (Object.keys(perMonth).length) next[s] = perMonth;
      });
      return next;
    });
  };

  // Default-select the first SKU once actuals settle.
  useEffect(() => {
    if (!sku && skuList.length) setSku(skuList[0]);
  }, [skuList, sku]);

  // Keep `selectedSkus` populated — default to whatever SKU is focused; the
  // user can then check additional SKUs in the multi-select popover.
  useEffect(() => {
    if (sku && !selectedSkus.includes(sku)) {
      setSelectedSkus((prev) => (prev.length ? prev : [sku]));
    }
  }, [sku, selectedSkus]);

  // -------------------- Reload planner on SKU / target change ---------------
  useEffect(() => {
    if (!sku || !target) return;
    const prevSku = prevSkuTargetRef.current.sku;

    // Snapshot outgoing SKU's edits so re-selecting it later restores them.
    if (prevSku && prevSku !== sku && params.length) {
      perSkuStateRef.current[prevSku] = {
        params: params.map((p) => ({ ...p })),
        channelPct: [...channelPct],
        channelPctManuallyEdited,
        weightMode,
        finalValue,
        manualFinal,
      };
    }

    // If we have a cached snapshot for the incoming SKU, restore it verbatim.
    const cached = perSkuStateRef.current[sku];
    if (cached && cached.params?.length) {
      setParams(cached.params.map((p) => ({ ...p })));
      setChannelPct([...cached.channelPct]);
      setChannelPctManuallyEdited(cached.channelPctManuallyEdited);
      setWeightMode(cached.weightMode);
      setFinalValue(cached.finalValue);
      setManualFinal(cached.manualFinal);
      prevSkuTargetRef.current = { sku, target };
      return;
    }

    // Otherwise compute fresh — preserving Manual-mode weights on SKU-switch.
    const isSwitch = prevSku && prevSku !== sku;
    const keep = (weightMode === 'manual' && params.length && isSwitch)
      ? params.map((p) => ({ name: p.name, weight: p.weight, use: p.use }))
      : null;

    const next = smartCalc(sku, target, newSkus, actualsRegistry, true);
    if (keep && keep.length === next.length) {
      next.forEach((p, i) => {
        if (keep[i].name === p.name) { p.weight = keep[i].weight; p.use = keep[i].use; }
      });
    } else if (weightMode === 'manual') {
      roundWeightsToWhole(next);
    }
    setParams(next);
    setManualFinal(false);
    prevSkuTargetRef.current = { sku, target };
  }, [sku, target, actualsRegistry]);

  // Recompute Final input whenever params / edits change.
  useEffect(() => {
    if (!params.length) return;
    const smart = calculateFromParams(params);
    if (!weightsValid(params)) return;
    const saved = editedProjections[editedKey(sku, target)];
    if (Number.isFinite(Number(saved))) {
      setFinalValue(Math.round(Number(saved)));
      setManualFinal(true);
    } else if (!manualFinal && Number.isFinite(smart)) {
      setFinalValue(Math.round(smart));
    }
  }, [params, sku, target]);

  // Auto-derive Channel Split from the SKU's latest 3 fed months.
  // Re-runs whenever the SKU, target month, or fed history changes.
  // Manual overrides win: once the user edits any channel %, we stop
  // overwriting until they switch SKU or click Reset.
  useEffect(() => {
    setChannelPctManuallyEdited(false);
  }, [sku, target]);

  useEffect(() => {
    if (channelPctManuallyEdited) return;
    const derived = computeChannelSplit(sku, target, channelActuals, CHANNELS);
    setChannelPct(derived);
  }, [sku, target, channelActuals, channelPctManuallyEdited]);

  // -------------------- Derived values --------------------------------------
  const smart = useMemo(() => calculateFromParams(params), [params]);
  const valid = useMemo(() => weightsValid(params), [params]);
  const totalWeight = useMemo(() => activeWeightTotal(params), [params]);
  const priorHist = useMemo(() => ACTUAL_MONTHS.filter((m) => monthIndex(m) < monthIndex(target)), [target]);
  // Sum of channel percentages — memoised so the tfoot doesn't recompute
  // it twice per render (className + display).
  const channelPctTotal = useMemo(
    () => channelPct.reduce((s, x) => s + x, 0),
    [channelPct]
  );
  // Smart Channel Split — engine-derived from fed history (or defaults).
  // Rendered separately from the user's editable "Current / Manual" split.
  const smartChannelPct = useMemo(
    () => computeChannelSplit(sku, target, channelActuals, CHANNELS),
    [sku, target, channelActuals]
  );
  // Effective channel split fed into per-channel unit calc respects the
  // Channels mode toggle: Smart → history, Manual → user edits, Mixed →
  // manual wins where present (today's behaviour).
  const effectiveChannelPct = useMemo(() => {
    if (modes.channels === 'smart') return smartChannelPct;
    if (modes.channels === 'manual') return channelPct;
    return channelPctManuallyEdited ? channelPct : smartChannelPct;
  }, [modes.channels, smartChannelPct, channelPct, channelPctManuallyEdited]);
  // Precomputed backtest rows for the History & Backtest table — cached so
  // the expensive per-month smartCalc doesn't re-run on every keystroke.
  const historyRows = useMemo(() => {
    return ALL_MONTHS.slice(1)
      .filter((m) => monthIndex(m) <= monthIndex(target))
      .map((m) => {
        const a = val(sku, m, actualsRegistry);
        const sp = smartCalc(sku, m, newSkus, actualsRegistry);
        const vr = Number.isFinite(a) ? a - sp : null;
        const vp = Number.isFinite(vr) && sp ? vr / sp * 100 : null;
        return { month: m, actual: a, smart: sp, variance: vr, variancePct: vp };
      });
  }, [sku, target, actualsRegistry, newSkus]);
  const actual = useMemo(() => val(sku, target, actualsRegistry), [sku, target, actualsRegistry]);
  const variance = (valid && Number.isFinite(actual) && Number.isFinite(smart)) ? actual - smart : null;
  const peerInfo = useMemo(() => {
    if (!sku || !target) return null;
    const sparse = isNewOrSparseSku(sku, target, actualsRegistry);
    const peers = sparse ? eligiblePeers(sku, target, newSkus, actualsRegistry) : [];
    return { sparse, peers, category: skuCategory(sku, newSkus), weight: extractWeight(sku, newSkus) };
  }, [sku, target, newSkus, actualsRegistry]);
  // Predictive forecast — only relevant when the SKU is sparse/new. Blends
  // peer avg + peer 3M trend + seasonality + category growth, weighted by
  // own SKU maturity (0 fresh → 1 at ≥6 months).
  const predictiveForecast = useMemo(() => {
    if (!sku || !target || !peerInfo?.sparse) return null;
    return predictSparseSku(sku, target, newSkus, actualsRegistry, channelActuals);
  }, [sku, target, newSkus, actualsRegistry, channelActuals, peerInfo]);

  // -------------------- Handlers --------------------------------------------
  const handleWeightModeChange = (mode) => {
    setWeightMode(mode);
    const next = [...params];
    if (mode === 'auto') normalizeAllWeights(next); else roundWeightsToWhole(next);
    setParams(next);
    setManualFinal(false);
  };

  const handleUseToggle = (i, on) => {
    const next = params.map((p, idx) => idx === i ? { ...p, use: on, weight: (on && (!Number.isFinite(p.weight) || p.weight <= 0)) ? 1 : p.weight } : p);
    if (weightMode === 'auto') normalizeAllWeights(next);
    setParams(next);
    setManualFinal(false);
  };

  const handleEditValue = (i, v) => {
    setParams((prev) => prev.map((p, idx) => idx === i ? { ...p, edit: parseFloat(v) || 0 } : p));
    setManualFinal(false);
  };

  const handleWeightEdit = (i, v) => {
    const next = [...params];
    if (weightMode === 'auto') {
      redistributeAfterEdit(next, i, v);
    } else {
      const edited = next[i];
      if (!edited?.use) return;
      edited.weight = Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
    }
    setParams(next);
    setManualFinal(false);
  };

  const handleFinalChange = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return;
    setFinalValue(Math.round(n));
    setManualFinal(true);
    const next = { ...editedProjections, [editedKey(sku, target)]: Math.round(n) };
    setEditedProjections(next);
    try { localStorage.setItem(EDITED_PROJECTIONS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const handleReset = () => {
    // Clear cached edits for this SKU too — so Reset means "back to Smart"
    // for the current SKU + target, regardless of any prior manual work.
    delete perSkuStateRef.current[sku];
    // Clear saved edit for current SKU × month, then re-run smart from scratch.
    const next = { ...editedProjections };
    delete next[editedKey(sku, target)];
    setEditedProjections(next);
    try { localStorage.setItem(EDITED_PROJECTIONS_KEY, JSON.stringify(next)); } catch { /* ignore */ }

    const fresh = smartCalc(sku, target, newSkus, actualsRegistry, true);
    if (weightMode === 'auto') normalizeAllWeights(fresh); else roundWeightsToWhole(fresh);
    setParams(fresh);
    setManualFinal(false);
    if (Number.isFinite(calculateFromParams(fresh))) {
      setFinalValue(Math.round(calculateFromParams(fresh)));
    }
    toast.success(`Reset to Smart Projection for ${sku} — ${target}`);
  };

  const handleAddSku = () => {
    const name = newSkuName.trim();
    const w = Number(newSkuWeight);
    if (!name || !Number.isFinite(w) || w <= 0) {
      toast.error('Enter a valid SKU name and grammage'); return;
    }
    const next = { ...newSkus, [name]: { category: newSkuCategory, weight: w } };
    setNewSkus(next);
    try { localStorage.setItem(NEW_SKU_STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    setActualsRegistry((prev) => ({ ...prev, [name]: prev[name] || {} }));
    setSku(name);
    setNewSkuName(''); setNewSkuWeight('');
    toast.success(`Added ${name} — projections will use same-category peers within ±50g`);
  };

  const handleExportOverall = () => {
    const skus = Object.keys(actualsRegistry).filter((s) => !isComboSku(s)).sort();
    const rows = [];
    skus.forEach((s) => {
      ALL_MONTHS.forEach((period) => {
        const [month, yy] = period.split("'");
        const year = yy ? 2000 + Number(yy) : '';
        const smartV = smartCalc(s, period, newSkus, actualsRegistry);
        const edited = editedProjections[editedKey(s, period)];
        const actualV = val(s, period, actualsRegistry);
        rows.push({
          'SKU Name': s, Month: month, Year: year,
          'Smart Projection': Number.isFinite(smartV) ? Math.round(smartV) : 0,
          'Edited Projection': (edited !== undefined && edited !== null && edited !== '') ? Math.round(Number(edited)) : '',
          'Actual Sales': Number.isFinite(actualV) ? Math.round(actualV) : '',
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ['SKU Name', 'Month', 'Year', 'Smart Projection', 'Edited Projection', 'Actual Sales'],
    });
    ws['!cols'] = [{ wch: 55 }, { wch: 10 }, { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SKU Projection History');
    XLSX.writeFile(wb, 'RADAR_SKU_Projection_History_All_SKUs.xlsx');
    toast.success('Overall SKU Excel exported');
  };

  const handleExportSelected = () => {
    if (!valid) { toast.error('Projection locked — total active weight must equal 100%'); return; }
    const final = finalValue || 0;
    const rows = [
      ['Projection Month', target],
      ['SKU', sku],
      ['Weight Editing Mode', weightMode === 'auto' ? 'Auto Balance' : 'Manual'],
      ['Total Active Weight', totalWeight.toFixed(2) + '%'],
      ['Actual Units', actual ?? ''],
      ['Smart Projection', Math.round(smart)],
      ['Final Editable Projection', Math.round(final)],
      [],
      ['Use', 'Parameter', 'Smart Value', 'Editable Value', 'Weight %', 'Logic'],
    ];
    params.forEach((p) => rows.push([p.use ? 'Yes' : 'No', p.name, p.smart, p.edit, p.weight, p.logic]));
    rows.push([], ['Month', 'Actual Units', 'Smart Projection', 'Variance', 'Variance %']);
    ALL_MONTHS.slice(1).filter((m) => monthIndex(m) <= monthIndex(target)).forEach((m) => {
      const a  = val(sku, m, actualsRegistry);
      const sp = smartCalc(sku, m, newSkus, actualsRegistry);
      const vr = Number.isFinite(a) ? a - sp : '';
      const vp = Number.isFinite(vr) && sp ? vr / sp * 100 : '';
      rows.push([m, a ?? '', Math.round(sp), Number.isFinite(vr) ? Math.round(vr) : '', Number.isFinite(vp) ? vp.toFixed(1) + '%' : '']);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `RADAR_${target.replace("'", '')}_${sku.replace(/[^a-z0-9]+/gi, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Selected SKU data exported');
  };

  // Base-weight sum for contribution calculation (mirrors source line 505-508).
  const baseW = params.filter((p) => p.use && p.type === 'base').reduce((s, p) => s + p.weight, 0);
  const baseVal = params.filter((p) => p.use && p.type === 'base')
    .reduce((s, p) => s + p.edit * (baseW ? p.weight / baseW : 0), 0);

  // -------------------- Render ----------------------------------------------
  return (
    <div className="p-6 space-y-6" data-testid="smart-projections-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-primary" />
            <h1 className="text-2xl font-bold text-wv-ink">Smart Projections</h1>
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
            SKU-wise dynamic projection engine · Actual sales history through {ACTUAL_MONTHS[ACTUAL_MONTHS.length - 1]} · Same-category ±50g peer logic for sparse / new SKUs · Auto/Manual weight modes · 100% weight validation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportSelected} data-testid="smart-export-selected">
            <Download size={14} className="mr-1.5" /> Download Selected
          </Button>
          <Button variant="outline" onClick={handleExportOverall} data-testid="smart-export-overall">
            <Download size={14} className="mr-1.5" /> Download Overall (Excel)
          </Button>
          <Button variant="secondary" onClick={handleReset} data-testid="smart-reset">
            <RotateCcw size={14} className="mr-1.5" /> Reset to Smart
          </Button>
        </div>
      </div>

      {/* Selectors + KPI cards */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-1">
          <CardContent className="p-4 space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Projection Month</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="smart-month-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {monthOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Brand</Label>
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger data-testid="smart-brand-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {brandOptions.map((b) => (
                    <SelectItem key={b} value={b}>{b === 'all' ? 'All brands' : b.charAt(0).toUpperCase() + b.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">SKU (focused)</Label>
              <Select value={sku} onValueChange={(v) => { setSku(v); setSelectedSkus((prev) => prev.includes(v) ? prev : [...prev, v]); }}>
                <SelectTrigger data-testid="smart-sku-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {visibleSkuList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <MultiSkuPicker
                options={visibleSkuList}
                selected={selectedSkus}
                onChange={setSelectedSkus}
              />
            </div>
            <div className="text-xs space-y-1 pt-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Historical Window</span><span className="font-semibold text-wv-ink">{priorHist.length ? `${priorHist[0]} → ${priorHist[priorHist.length - 1]}` : '—'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Actual for {target}</span><span className="font-semibold text-wv-ink">{Number.isFinite(actual) ? fmt(actual) : 'Not available'}</span></div>
            </div>
            <div className={`text-xs font-semibold px-2 py-1.5 rounded ${valid ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
                 data-testid="smart-weight-status">
              {valid
                ? `Total active weight: 100.00% — projection enabled (${weightMode === 'auto' ? 'Auto' : 'Manual'} mode)`
                : `Projection locked: total active weight is ${totalWeight.toFixed(2)}%. Adjust to exactly 100%.`}
            </div>
            <div className="flex items-center gap-1.5 pt-1">
              <Button size="sm" variant={weightMode === 'auto' ? 'default' : 'outline'} onClick={() => handleWeightModeChange('auto')} data-testid="smart-mode-auto">Auto Balance</Button>
              <Button size="sm" variant={weightMode === 'manual' ? 'default' : 'outline'} onClick={() => handleWeightModeChange('manual')} data-testid="smart-mode-manual">Manual</Button>
            </div>
          </CardContent>
        </Card>

        <KpiCard label="Smart Projection" value={valid ? fmt(smart) : 'LOCKED'} sub="System-calculated blend" tone="teal" testid="smart-kpi-smart" />

        <Card>
          <CardHeader className="pb-2">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Final Editable Projection</p>
          </CardHeader>
          <CardContent className="pt-0">
            <Input
              type="number"
              value={valid ? finalValue : ''}
              onChange={(e) => handleFinalChange(e.target.value)}
              disabled={!valid}
              className="text-2xl font-bold text-center bg-primary/5 border-primary/30"
              data-testid="smart-final-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Editable business override (persisted)</p>
          </CardContent>
        </Card>

        <KpiCard
          label="Backtest Variance"
          value={Number.isFinite(variance) ? `${variance >= 0 ? '+' : ''}${fmt(variance)}` : '—'}
          sub="Actual − Smart, when actual exists"
          tone={Number.isFinite(variance) ? (variance >= 0 ? 'green' : 'red') : 'slate'}
          testid="smart-kpi-variance"
        />
      </div>

      {/* Add New / Unfed SKU */}
      <AddSkuBlock
        newSkuName={newSkuName} setNewSkuName={setNewSkuName}
        newSkuCategory={newSkuCategory} setNewSkuCategory={setNewSkuCategory}
        newSkuWeight={newSkuWeight} setNewSkuWeight={setNewSkuWeight}
        onAdd={handleAddSku}
        peerInfo={peerInfo}
      />

      {/* Predictive Forecast — appears when the current SKU is sparse/new. */}
      {predictiveForecast && (
        <PredictiveForecastCard sku={sku} target={target} forecast={predictiveForecast} />
      )}

      {/* Parameters table */}
      <Card>
        <CardHeader className="py-3 bg-wv-ink text-white rounded-t-md">
          <p className="text-sm font-semibold">Smart Projection Parameters</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[440px] overflow-auto">
            <table className="w-full text-xs" data-testid="smart-params-table">
              <thead className="bg-slate-800 text-white sticky top-0 z-10">
                <tr>
                  <th className="text-left px-2 py-2 font-semibold">Use?</th>
                  <th className="text-left px-2 py-2 font-semibold">Parameter</th>
                  <th className="text-right px-2 py-2 font-semibold">Smart Value</th>
                  <th className="text-right px-2 py-2 font-semibold">Editable Value</th>
                  <th className="text-right px-2 py-2 font-semibold">Weight %</th>
                  <th className="text-right px-2 py-2 font-semibold">Contribution</th>
                  <th className="text-left px-2 py-2 font-semibold">Logic</th>
                </tr>
              </thead>
              <tbody>
                {params.map((p, i) => {
                  const contrib = valid
                    ? (p.type === 'base'
                        ? (p.use ? p.edit * (baseW ? p.weight / baseW : 0) : 0)
                        : (p.use ? baseVal * (p.edit / 100) * (p.weight / 100) : 0))
                    : null;
                  return (
                    <tr key={p.name} className="border-t border-border hover:bg-muted/30" data-testid={`smart-param-row-${i}`}>
                      <td className="px-2 py-1.5">
                        <Select value={p.use ? 'Yes' : 'No'} onValueChange={(v) => handleUseToggle(i, v === 'Yes')}>
                          <SelectTrigger className="h-7 w-16 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-1.5 font-semibold text-wv-ink">{p.name}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{p.type === 'driver' ? `${p.smart.toFixed(1)}%` : fmt(p.smart)}</td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step={p.type === 'driver' ? '0.1' : '1'}
                          value={p.type === 'driver' ? p.edit.toFixed(1) : Math.round(p.edit)}
                          onChange={(e) => handleEditValue(i, e.target.value)}
                          className="h-7 text-xs text-right" />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step={weightMode === 'manual' ? '1' : '0.1'} min="0" max="100"
                          value={weightMode === 'manual' ? Math.round(p.weight) : p.weight.toFixed(2)}
                          onChange={(e) => handleWeightEdit(i, e.target.value)}
                          className="h-7 text-xs text-right" />
                      </td>
                      <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${Number.isFinite(contrib) ? (contrib < 0 ? 'text-red-600' : 'text-green-700') : 'text-red-600'}`}>
                        {Number.isFinite(contrib) ? fmt(contrib) : 'LOCKED'}
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">{p.logic}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/40 font-bold">
                  <td colSpan={4} className="px-2 py-2">TOTAL ACTIVE WEIGHT</td>
                  <td className="px-2 py-2 text-right tabular-nums">{totalWeight.toFixed(2)}%</td>
                  <td colSpan={2} className={`px-2 py-2 ${valid ? 'text-green-700' : 'text-red-600'}`}>{valid ? 'Valid' : 'Must equal 100%'}</td>
                </tr>
                <tr className="bg-muted/60 font-bold">
                  <td colSpan={5} className="px-2 py-2">SMART PROJECTION</td>
                  <td className="px-2 py-2 text-right tabular-nums">{valid ? fmt(smart) : 'LOCKED'}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* History + Channel Split (side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3">
          <CardHeader className="py-3 bg-wv-ink text-white rounded-t-md">
            <p className="text-sm font-semibold">Month-wise SKU History & Backtest</p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[440px] overflow-auto">
              <table className="w-full text-xs" data-testid="smart-history-table">
                <thead className="bg-slate-800 text-white sticky top-0"><tr>
                  <th className="text-left px-2 py-2">Month</th>
                  <th className="text-right px-2 py-2">Actual Units</th>
                  <th className="text-right px-2 py-2">Smart Projection</th>
                  <th className="text-right px-2 py-2">Variance</th>
                  <th className="text-right px-2 py-2">Variance %</th>
                </tr></thead>
                <tbody>
                  {historyRows.map(({ month: m, actual: a, smart: sp, variance: vr, variancePct: vp }) => (
                    <tr key={m} className="border-t border-border">
                      <td className="px-2 py-1.5 font-semibold">{m}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{Number.isFinite(a) ? fmt(a) : '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(sp)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${Number.isFinite(vr) ? (vr >= 0 ? 'text-green-700' : 'text-red-600') : ''}`}>{Number.isFinite(vr) ? `${vr >= 0 ? '+' : ''}${fmt(vr)}` : '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{Number.isFinite(vp) ? `${vp.toFixed(1)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="py-3 bg-wv-ink text-white rounded-t-md flex flex-row items-center justify-between">
            <p className="text-sm font-semibold">Channel Split — Smart vs Current</p>
            <ChannelSplitBadge sku={sku} target={target} channelActuals={channelActuals}
              manuallyEdited={channelPctManuallyEdited}
              onReset={() => setChannelPctManuallyEdited(false)} />
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-xs" data-testid="smart-channels-table">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="text-left px-2 py-2">Channel</th>
                  <th className="text-right px-2 py-2" title="History-derived from latest 3 fed months">Smart %</th>
                  <th className="text-right px-2 py-2" title="User-editable">Current / Manual %</th>
                  <th className="text-right px-2 py-2">Effective %</th>
                  <th className="text-right px-2 py-2">Projected Units</th>
                </tr>
              </thead>
              <tbody>
                {!valid && (
                  <tr><td colSpan={5} className="text-center py-3 text-red-600 font-semibold">
                    Projection locked until total active weight equals 100.00%
                  </td></tr>
                )}
                {valid && CHANNELS.map((c, i) => {
                  const units = Math.round((finalValue || 0) * effectiveChannelPct[i] / 100);
                  return (
                    <tr key={c[0]} className="border-t border-border">
                      <td className="px-2 py-1.5 font-semibold">{c[0]}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-primary">{smartChannelPct[i].toFixed(1)}%</td>
                      <td className="px-2 py-1.5">
                        <Input type="number" step="0.1" value={channelPct[i].toFixed(1)}
                          onChange={(e) => {
                            setChannelPctManuallyEdited(true);
                            setChannelPct((prev) => prev.map((x, idx) => idx === i ? (parseFloat(e.target.value) || 0) : x));
                          }}
                          disabled={modes.channels === 'smart'}
                          className="h-7 text-xs text-right" />
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{effectiveChannelPct[i].toFixed(1)}%</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmt(units)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/40 font-bold">
                <tr className="border-t border-border">
                  <td className="px-2 py-2">Total</td>
                  <td className="px-2 py-2 text-right tabular-nums">{smartChannelPct.reduce((s, x) => s + x, 0).toFixed(1)}%</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${Math.abs(channelPctTotal - 100) < 0.1 ? 'text-green-700' : 'text-red-600'}`}>
                    {channelPctTotal.toFixed(1)}%
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">{effectiveChannelPct.reduce((s, x) => s + x, 0).toFixed(1)}%</td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {valid ? fmt(CHANNELS.reduce((s, _, i) => s + Math.round((finalValue || 0) * effectiveChannelPct[i] / 100), 0)) : 'LOCKED'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Three-axis mode selector — governs which values fold into rendering. */}
      <ModeSelector modes={modes} onChange={setModes} />

      {/* Multi-SKU consolidated table (only when >1 SKU is selected) */}
      {selectedSkus.length > 1 && (
        <MultiSkuTable
          selectedSkus={selectedSkus}
          target={target}
          newSkus={newSkus}
          actualsRegistry={actualsRegistry}
          channelActuals={channelActuals}
          perSkuState={perSkuStateRef.current}
          editedProjections={editedProjections}
          onDeselect={(s) => setSelectedSkus((prev) => prev.filter((x) => x !== s))}
        />
      )}

      <p className="text-[11px] text-muted-foreground">
        Backend audit: {skuList.length} mapped SKUs across {ACTUAL_MONTHS.length} actual months. Sparse SKU rule preserved: &lt;6 positive-actual months → same-category peers within ±50g; no eligible peer → projection 0.
      </p>

      {/* Monthly SKU × Channel comparison — Smart vs Actual vs Gap */}
      <MonthlyComparisonTable
        skuList={skuList}
        newSkus={newSkus}
        actualsRegistry={actualsRegistry}
        channelActuals={channelActuals}
        defaultMonth={target}
      />

      {/* Data Feeding Center — persistent actuals ingestion */}
      <DataFeedingCenter
        skuList={skuList}
        onSaved={applyOverrides}
      />
    </div>
  );
}

/* ---------- Sub-components ------------------------------------------------- */

function KpiCard({ label, value, sub, tone = 'slate', testid }) {
  const toneClass = {
    teal:  'bg-primary/10 text-primary border-primary/30',
    green: 'bg-green-50 text-green-700 border-green-200',
    red:   'bg-red-50 text-red-600 border-red-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  }[tone];
  return (
    <Card>
      <CardHeader className="pb-2">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className={`text-2xl font-bold rounded px-2 py-2 text-center border ${toneClass}`} data-testid={testid}>{value}</div>
        <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>
      </CardContent>
    </Card>
  );
}

function ChannelSplitBadge({ sku, target, channelActuals, manuallyEdited, onReset }) {
  const fedMonths = sku && channelActuals?.[sku]
    ? Object.entries(channelActuals[sku])
        .filter(([m, v]) => monthIndex(m) < monthIndex(target) && v?.channels && v.total > 0)
        .sort((a, b) => monthIndex(a[0]) - monthIndex(b[0]))
        .slice(-3)
        .map(([m]) => m)
    : [];
  if (manuallyEdited) {
    return (
      <button type="button" onClick={onReset}
        className="text-[10px] px-2 py-0.5 rounded bg-amber-500 hover:bg-amber-400 text-black font-semibold"
        data-testid="channel-split-reset">
        Manual override · reset to history
      </button>
    );
  }
  if (fedMonths.length === 0) {
    return <span className="text-[10px] px-2 py-0.5 rounded bg-slate-500 font-semibold">Using default mix</span>;
  }
  return (
    <span className="text-[10px] px-2 py-0.5 rounded bg-primary/80 font-semibold" data-testid="channel-split-source">
      From history: {fedMonths.join(' · ')}
    </span>
  );
}

function AddSkuBlock({ newSkuName, setNewSkuName, newSkuCategory, setNewSkuCategory, newSkuWeight, setNewSkuWeight, onAdd, peerInfo }) {
  const isSparse = !!peerInfo?.sparse;
  const noPeer = isSparse && !peerInfo.peers.length;
  return (
    <Card>
      <CardHeader className="py-3 bg-wv-ink text-white rounded-t-md">
        <p className="text-sm font-semibold">Add New / Unfed SKU</p>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">SKU Name</Label>
            <Input value={newSkuName} onChange={(e) => setNewSkuName(e.target.value)} placeholder="Example: Creatine Cherry (120g)" data-testid="smart-new-sku-name" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Category</Label>
            <Select value={newSkuCategory} onValueChange={setNewSkuCategory}>
              <SelectTrigger data-testid="smart-new-sku-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Grammage</Label>
            <Input type="number" min="1" value={newSkuWeight} onChange={(e) => setNewSkuWeight(e.target.value)} placeholder="120" data-testid="smart-new-sku-weight" />
          </div>
          <Button onClick={onAdd} data-testid="smart-add-sku"><Plus size={14} className="mr-1" /> Add SKU</Button>
        </div>

        {peerInfo && !isSparse && (
          <div className="flex items-start gap-2 text-xs px-3 py-2 rounded border border-blue-200 bg-blue-50 text-blue-800">
            <Info size={14} className="mt-0.5 shrink-0" />
            This SKU has at least 6 positive-sales months. Its own historical data is being used.
          </div>
        )}
        {peerInfo && isSparse && !noPeer && (
          <div className="text-xs px-3 py-2 rounded border border-blue-200 bg-blue-50 text-blue-800 space-y-1">
            <div className="flex items-start gap-2"><Info size={14} className="mt-0.5 shrink-0" />
              <span>Sparse SKU peer logic active: <b>{peerInfo.peers.length}</b> same-category peer SKU(s) found within ±50g.</span></div>
            <div className="text-[11px] text-blue-700 pl-5">Reference peers: {peerInfo.peers.join(', ')}</div>
          </div>
        )}
        {peerInfo && noPeer && (
          <div className="flex items-start gap-2 text-xs px-3 py-2 rounded border border-red-200 bg-red-50 text-red-700">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            No eligible same-category SKU within ±50g with ≥6 months of actual data. <b>Smart projection is 0.</b>
          </div>
        )}
        {!peerInfo && (
          <Badge variant="secondary" className="text-[11px]">
            Every SKU with fewer than 6 positive-sales months uses the monthly average of same-category SKUs within ±50g.
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------- Data Feeding Center -------------------------------------------
 * Bulk-ingests actual monthly sales into the projection engine. Ops picks
 * Month + Year, enters SKU rows manually or drops in an Excel file, and
 * saves — the backend validates + upserts, the engine's registry is
 * refreshed, and every downstream calculation (Smart, backtest, peer
 * discovery) sees the new data immediately. No other RADAR tab is touched.
 */

const MONTH_LABELS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const YEAR_LABELS = ['25', '26', '27', '28'];

function DataFeedingCenter({ skuList, onSaved }) {
  const [month, setMonth] = useState('AUG');
  const [year, setYear] = useState('26');
  // Each row now carries a per-channel unit map; total is auto-computed.
  const emptyRow = () => ({
    id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    sku: '',
    channels: Object.fromEntries(CHANNELS.map((c) => [c[0], ''])),
  });
  const [entries, setEntries] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [pendingSave, setPendingSave] = useState(null);
  const [summary, setSummary] = useState(null);
  const fileInputRef = useRef(null);

  const monthToken = `${month}'${year}`;
  const skuSet = useMemo(() => new Set(skuList), [skuList]);
  const channelNames = useMemo(() => CHANNELS.map((c) => c[0]), []);

  const rowTotal = (row) => channelNames.reduce((s, ch) => s + (Number(row.channels?.[ch]) || 0), 0);

  const addRow = () => setEntries((e) => [...e, emptyRow()]);
  const removeRow = (idx) => setEntries((e) => e.filter((_, i) => i !== idx));
  const updateSku = (idx, sku) =>
    setEntries((e) => e.map((row, i) => i === idx ? { ...row, sku } : row));
  const updateChannel = (idx, channel, value) =>
    setEntries((e) => e.map((row, i) => i === idx
      ? { ...row, channels: { ...row.channels, [channel]: value } }
      : row));

  // Case-insensitive channel-column lookup on the parsed Excel row so ops can
  // spell "amazon uae" as "Amazon UAE" or "AMAZON UAE" — no ceremony.
  const pickChannelFromRow = (row, channelName) => {
    const target = String(channelName).trim().toLowerCase();
    for (const key of Object.keys(row)) {
      if (String(key).trim().toLowerCase() === target) {
        const raw = row[key];
        const n = Number(String(raw ?? '').replace(/,/g, '').trim());
        return Number.isFinite(n) ? n : '';
      }
    }
    return '';
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const parsed = [];
      rows.forEach((r) => {
        const sku = String(r['SKU Name'] ?? r['SKU'] ?? r['sku'] ?? '').trim();
        if (!sku) return;
        const channels = {};
        channelNames.forEach((ch) => { channels[ch] = pickChannelFromRow(r, ch); });
        // Fallback: if the sheet only carries an "Actual Units" total (legacy
        // format), map the whole value onto Amazon so the row isn't dropped.
        const totalOnly = r['Actual Units'] ?? r['Units'] ?? r['units'];
        if (totalOnly !== undefined && totalOnly !== ''
            && !channelNames.some((ch) => channels[ch] !== '' && Number(channels[ch]) > 0)) {
          channels[channelNames[0]] = Number(String(totalOnly).replace(/,/g, '').trim()) || 0;
        }
        parsed.push({
          id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          sku, channels,
        });
      });
      if (!parsed.length) {
        toast.error('No SKU rows found. Expected columns: "SKU Name" and one column per channel (Amazon, Flipkart, W.in, …).');
        return;
      }
      setEntries(parsed);
      toast.success(`Loaded ${parsed.length} rows from ${file.name}`);
    } catch (e) {
      toast.error(`Failed to parse Excel: ${e.message}`);
    }
  };

  const validate = () => {
    const seen = new Map();
    const invalid = [];
    const cleaned = [];
    entries.forEach((row, i) => {
      const sku = String(row.sku || '').trim();
      if (!sku) { invalid.push({ row: i + 1, reason: 'missing SKU' }); return; }
      if (!skuSet.has(sku)) { invalid.push({ row: i + 1, sku, reason: 'unknown SKU — add it via "Add New / Unfed SKU" first' }); return; }
      // Reject rows where any channel is negative or non-numeric-that-was-typed
      let ok = true;
      const channels = {};
      channelNames.forEach((ch) => {
        const raw = row.channels?.[ch];
        if (raw === '' || raw === null || raw === undefined) return; // treat blank as zero, but drop from payload
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) {
          invalid.push({ row: i + 1, sku, reason: `invalid units on ${ch}` });
          ok = false;
          return;
        }
        if (n > 0) channels[ch] = n;
      });
      if (!ok) return;
      const total = Object.values(channels).reduce((s, v) => s + v, 0);
      if (total <= 0) { invalid.push({ row: i + 1, sku, reason: 'at least one channel must be > 0' }); return; }
      if (seen.has(sku)) { invalid.push({ row: i + 1, sku, reason: 'duplicate SKU in this batch' }); return; }
      seen.set(sku, total);
      cleaned.push({ sku, month: monthToken, channels });
    });
    return { cleaned, invalid };
  };

  const submit = async (overwrite = false, payload = null) => {
    const { cleaned, invalid } = payload ? { cleaned: payload, invalid: [] } : validate();
    if (invalid.length && !payload) {
      toast.error(`${invalid.length} invalid row(s) — check the summary below`);
      setSummary({ ok: false, saved: 0, replaced: 0, invalid });
      return;
    }
    if (!cleaned.length) { toast.error('Nothing to save'); return; }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/smart-projections/overrides`,
        { entries: cleaned, overwrite },
        { headers: authHeaders() },
      );
      const data = res.data;
      if (data.status === 'conflicts' && !overwrite) {
        setConflicts(data.conflicts);
        setPendingSave(cleaned);
        return;
      }
      const refresh = await axios.get(`${API}/smart-projections/overrides`, { headers: authHeaders() });
      onSaved(refresh.data?.overrides || {});
      setSummary({
        ok: true,
        saved: data.saved || 0,
        replaced: data.replaced || 0,
        invalid: [...(data.invalid || []), ...invalid],
      });
      toast.success(`Saved ${data.saved} new · replaced ${data.replaced} · ${(data.invalid || []).length} rejected`);
      setConflicts([]); setPendingSave(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="py-3 bg-wv-ink text-white rounded-t-md flex flex-row items-center gap-2">
        <Database size={16} />
        <p className="text-sm font-semibold">Data Feeding Center — SKU × Channel Actuals</p>
        <Badge variant="secondary" className="ml-2 text-[10px]">Feeds engine · backtests · peer discovery · dynamic channel split</Badge>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="feed-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTH_LABELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="feed-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEAR_LABELS.map((y) => <SelectItem key={y} value={y}>{`20${y}`}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
              onChange={(e) => handleFile(e.target.files?.[0])} data-testid="feed-file-input" />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="feed-upload">
              <Upload size={14} className="mr-1.5" /> Upload Excel
            </Button>
          </div>
          <Button onClick={() => submit(false)} disabled={saving} data-testid="feed-save">
            <Save size={14} className="mr-1.5" /> {saving ? 'Saving…' : `Save ${monthToken}`}
          </Button>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Excel columns expected: <b>SKU Name</b> plus one column per channel ({channelNames.join(', ')}). Blank channel cells count as zero. Total is derived automatically. Existing month values require confirmation before replacement — no other months are ever touched.
        </p>

        {/* Manual entry rows — SKU pinned left, channels + Total scroll horizontally */}
        <div className="border border-border rounded-md overflow-auto max-h-96">
          <table className="text-xs" style={{ minWidth: '100%' }} data-testid="feed-entries-table">
            <thead className="bg-slate-800 text-white sticky top-0 z-10">
              <tr>
                <th className="text-left px-2 py-1.5 w-8">#</th>
                <th className="text-left px-2 py-1.5 min-w-[280px] sticky left-0 bg-slate-800 z-20">SKU</th>
                {channelNames.map((ch) => (
                  <th key={ch} className="text-right px-2 py-1.5 min-w-[90px]">{ch}</th>
                ))}
                <th className="text-right px-2 py-1.5 min-w-[90px] bg-primary/20">Total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {entries.map((row, i) => {
                const total = rowTotal(row);
                return (
                  <tr key={row.id ?? `entry-${i}`} className="border-t border-border">
                    <td className="px-2 py-1.5 text-muted-foreground">{i + 1}</td>
                    <td className="px-2 py-1.5 sticky left-0 bg-background z-10">
                      <Input list="feed-sku-options" value={row.sku}
                        onChange={(e) => updateSku(i, e.target.value)}
                        placeholder="SKU (exact master-list spelling)"
                        className="h-7 text-xs min-w-[260px]" data-testid={`feed-sku-${i}`} />
                    </td>
                    {channelNames.map((ch) => (
                      <td key={ch} className="px-2 py-1.5">
                        <Input type="number" min="0" value={row.channels[ch] ?? ''}
                          onChange={(e) => updateChannel(i, ch, e.target.value)}
                          className="h-7 text-xs text-right w-[80px]"
                          data-testid={`feed-ch-${i}-${ch.replace(/\s+/g, '-').toLowerCase()}`} />
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold bg-primary/5">{fmt(total)}</td>
                    <td className="px-2 py-1.5 text-center">
                      <button type="button" className="text-red-600 hover:underline text-xs"
                        onClick={() => removeRow(i)}>×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <datalist id="feed-sku-options">
            {skuList.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <Button variant="outline" size="sm" onClick={addRow} data-testid="feed-add-row">
          <Plus size={14} className="mr-1" /> Add row
        </Button>

        {summary && (
          <div className={`text-xs px-3 py-2 rounded border ${
            summary.ok
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`} data-testid="feed-summary">
            {summary.ok
              ? <div className="font-semibold">✓ Saved {summary.saved} new · replaced {summary.replaced} · channel splits refreshed</div>
              : <div className="font-semibold">✗ {summary.invalid.length} row(s) rejected — fix and retry</div>}
            {summary.invalid?.length > 0 && (
              <ul className="mt-1 pl-4 list-disc">
                {summary.invalid.slice(0, 8).map((iv, i) =>
                  <li key={i}>Row {iv.row}{iv.sku ? ` (${iv.sku})` : ''}: {iv.reason}</li>)}
                {summary.invalid.length > 8 && <li>… and {summary.invalid.length - 8} more</li>}
              </ul>
            )}
          </div>
        )}
      </CardContent>

      {/* Conflict confirmation dialog */}
      <Dialog open={conflicts.length > 0} onOpenChange={(o) => !o && setConflicts([])}>
        <DialogContent className="max-w-2xl" data-testid="feed-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              Confirm Replacement — {conflicts.length} existing value(s)
            </DialogTitle>
            <DialogDescription>
              These (SKU, month) pairs already have saved actuals. Confirm to overwrite; other months remain untouched.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-auto border rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-slate-800 text-white sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">SKU</th>
                  <th className="text-left px-2 py-1.5">Month</th>
                  <th className="text-right px-2 py-1.5">Existing Total</th>
                  <th className="text-right px-2 py-1.5">New Total</th>
                </tr>
              </thead>
              <tbody>
                {conflicts.map((c) => (
                  <tr key={`${c.sku}-${c.month}`} className="border-t border-border">
                    <td className="px-2 py-1.5">{c.sku}</td>
                    <td className="px-2 py-1.5">{c.month}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(c.existing)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-primary">{fmt(c.new)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConflicts([]); setPendingSave(null); }} data-testid="feed-conflict-cancel">Cancel</Button>
            <Button onClick={() => submit(true, pendingSave)} disabled={saving} data-testid="feed-conflict-confirm">
              {saving ? 'Replacing…' : 'Replace values'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------- Multi-SKU selection popover ------------------------------------
 * Compact checkbox list that keeps `selectedSkus` in sync. Rendered under
 * the focused-SKU dropdown so the two selectors share the same visual lane.
 */
function MultiSkuPicker({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (s) => onChange(selected.includes(s) ? selected.filter((x) => x !== s) : [...selected, s]);
  const allSelected = selected.length === options.length && options.length > 0;
  return (
    <div className="mt-1 relative">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full text-left text-[11px] px-2 py-1 border border-dashed border-primary/40 rounded bg-primary/5 hover:bg-primary/10"
        data-testid="smart-multi-sku-toggle">
        + Add / remove SKUs · <b>{selected.length}</b> selected
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-80 overflow-auto rounded-md border border-border bg-background shadow-lg p-2 space-y-1"
          data-testid="smart-multi-sku-panel">
          <div className="flex items-center justify-between border-b border-border pb-1 mb-1">
            <button type="button" onClick={() => onChange(allSelected ? [] : options.slice())}
              className="text-[11px] text-primary hover:underline">
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-muted-foreground hover:text-wv-ink">Close</button>
          </div>
          {options.map((s) => (
            <label key={s} className="flex items-center gap-2 text-xs px-1 py-0.5 hover:bg-muted rounded cursor-pointer">
              <input type="checkbox" checked={selected.includes(s)} onChange={() => toggle(s)}
                data-testid={`smart-multi-sku-opt-${s.slice(0, 20).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} />
              <span className="truncate">{s}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Three-axis Mode Selector --------------------------------------
 * Governs how the page renders values along three independent axes:
 * Projection, Parameters, and Channel Split. Modes: Smart / Manual / Mixed.
 */
function ModeSelector({ modes, onChange }) {
  const axes = [
    { key: 'projection', label: 'Overall Projection' },
    { key: 'params',     label: 'Parameters & Weights' },
    { key: 'channels',   label: 'Channel Split' },
  ];
  return (
    <Card>
      <CardHeader className="py-3 bg-wv-ink text-white rounded-t-md">
        <p className="text-sm font-semibold">Layered Mode — Smart · Manual · Mixed</p>
      </CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {axes.map(({ key, label }) => (
            <div key={key}>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">{label}</p>
              <div className="flex items-center gap-1.5" data-testid={`mode-${key}`}>
                {['smart', 'manual', 'mixed'].map((m) => (
                  <Button key={m} size="sm"
                    variant={modes[key] === m ? 'default' : 'outline'}
                    onClick={() => onChange({ ...modes, [key]: m })}
                    data-testid={`mode-${key}-${m}`}>
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          <b>Smart</b> = engine outputs only (edits preserved but ignored). <b>Manual</b> = your edits win outright. <b>Mixed</b> = engine baseline with your overrides winning where present (default).
        </p>
      </CardContent>
    </Card>
  );
}

/* ---------- Multi-SKU consolidated table ----------------------------------
 * Read-only overview of Smart / Final / Actual / top-channel-split for every
 * selected SKU. CSV download exports the full detail (all channels).
 */
function MultiSkuTable({ selectedSkus, target, newSkus, actualsRegistry, channelActuals, perSkuState, editedProjections, onDeselect }) {
  const rows = useMemo(() => selectedSkus.map((s) => {
    const smart = smartCalc(s, target, newSkus, actualsRegistry);
    const savedFinal = editedProjections[`${s}|||${target}`];
    const cached = perSkuState[s];
    const finalV = Number.isFinite(Number(savedFinal))
      ? Number(savedFinal)
      : (cached && cached.manualFinal ? cached.finalValue : smart);
    const actual = val(s, target, actualsRegistry);
    const pct = computeChannelSplit(s, target, channelActuals, CHANNELS);
    return { sku: s, smart, finalV, actual, pct };
  }), [selectedSkus, target, newSkus, actualsRegistry, channelActuals, perSkuState, editedProjections]);

  const totals = useMemo(() => {
    const t = { smart: 0, finalV: 0, actual: 0 };
    rows.forEach((r) => {
      t.smart += Number.isFinite(r.smart) ? r.smart : 0;
      t.finalV += Number.isFinite(r.finalV) ? r.finalV : 0;
      t.actual += Number.isFinite(r.actual) ? r.actual : 0;
    });
    return t;
  }, [rows]);

  const download = () => {
    const header = ['SKU', 'Target Month', 'Smart Projection', 'Final Projection', `Actual (${target})`, ...CHANNELS.map((c) => `${c[0]} %`), ...CHANNELS.map((c) => `${c[0]} Units (Final × %)`)];
    const csv = [header.join(',')];
    rows.forEach((r) => {
      const chUnits = CHANNELS.map((_, i) => Math.round((Number(r.finalV) || 0) * r.pct[i] / 100));
      csv.push([
        `"${r.sku.replace(/"/g, '""')}"`, target,
        Number.isFinite(r.smart) ? Math.round(r.smart) : '',
        Number.isFinite(r.finalV) ? Math.round(r.finalV) : '',
        Number.isFinite(r.actual) ? Math.round(r.actual) : '',
        ...r.pct.map((p) => p.toFixed(2)),
        ...chUnits,
      ].join(','));
    });
    const blob = new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `RADAR_MultiSKU_${target.replace("'", '')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Downloaded ${rows.length} SKUs`);
  };

  return (
    <Card>
      <CardHeader className="py-3 bg-wv-ink text-white rounded-t-md flex flex-row items-center justify-between">
        <p className="text-sm font-semibold">Multi-SKU Overview — {rows.length} SKUs · {target}</p>
        <Button size="sm" variant="secondary" onClick={download} data-testid="multi-sku-download">
          <Download size={14} className="mr-1.5" /> Download CSV
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full text-xs" data-testid="multi-sku-table">
            <thead className="bg-slate-800 text-white sticky top-0">
              <tr>
                <th className="text-left px-2 py-2">SKU</th>
                <th className="text-right px-2 py-2">Smart</th>
                <th className="text-right px-2 py-2">Final</th>
                <th className="text-right px-2 py-2">Actual</th>
                <th className="text-left px-2 py-2">Top-3 Channel Split</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const idx = [...r.pct.keys()].sort((a, b) => r.pct[b] - r.pct[a]).slice(0, 3);
                const top3 = idx.map((i) => `${CHANNELS[i][0]} ${r.pct[i].toFixed(0)}%`).join(' · ');
                return (
                  <tr key={r.sku} className="border-t border-border hover:bg-muted/30">
                    <td className="px-2 py-1.5 font-medium">{r.sku}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.smart)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{fmt(r.finalV)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{Number.isFinite(r.actual) ? fmt(r.actual) : '—'}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{top3}</td>
                    <td className="px-2 py-1.5 text-center">
                      <button type="button" className="text-red-600 hover:underline text-xs" onClick={() => onDeselect(r.sku)}>×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-muted/40 font-bold">
              <tr className="border-t border-border">
                <td className="px-2 py-2">TOTAL</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(totals.smart)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(totals.finalV)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(totals.actual)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------- Monthly SKU × Channel comparison ------------------------------
 * Long-format table: one row per (SKU × Channel) for the selected month.
 * Columns: SKU · Brand · Channel · Smart Units · Actual Units · Gap.
 * View mode toggles which numeric columns are visible; multi-select
 * filters trim the rows; Excel download exports the currently-visible set.
 * All numbers come from the existing engine + fed channel actuals — no
 * new formulas.
 */
function MonthlyComparisonTable({ skuList, newSkus, actualsRegistry, channelActuals, defaultMonth }) {
  const [month, setMonth] = useState(defaultMonth);
  const [view, setView] = useState('all'); // 'smart' | 'actual' | 'gap' | 'all'
  const [brandFilter, setBrandFilter] = useState([]);   // [] = all
  const [skuFilter, setSkuFilter] = useState([]);
  const [channelFilter, setChannelFilter] = useState([]);
  const monthOptions = useMemo(() => ALL_MONTHS.slice(1), []);
  const channelNames = useMemo(() => CHANNELS.map((c) => c[0]), []);
  const brandOptions = useMemo(() => {
    const b = new Set();
    skuList.forEach((s) => { const c = skuCategory(s, newSkus); if (c) b.add(c); });
    return Array.from(b).sort();
  }, [skuList, newSkus]);

  useEffect(() => { setMonth(defaultMonth); }, [defaultMonth]);

  const isBrandActive = (b) => brandFilter.length === 0 || brandFilter.includes(b);
  const isSkuActive = (s) => skuFilter.length === 0 || skuFilter.includes(s);
  const isChannelActive = (c) => channelFilter.length === 0 || channelFilter.includes(c);

  // Compute per-SKU Smart units and channel split ONCE per SKU, then fan
  // out to channel rows.
  const rows = useMemo(() => {
    const out = [];
    skuList.forEach((s) => {
      const brand = skuCategory(s, newSkus) || '—';
      if (!isBrandActive(brand) || !isSkuActive(s)) return;
      const smartTotal = smartCalc(s, month, newSkus, actualsRegistry);
      const smartPct = computeChannelSplit(s, month, channelActuals, CHANNELS);
      const fedChannels = channelActuals?.[s]?.[month]?.channels;
      const fedTotal = channelActuals?.[s]?.[month]?.total;
      CHANNELS.forEach((c, i) => {
        const chName = c[0];
        if (!isChannelActive(chName)) return;
        const smartU = Math.round((Number(smartTotal) || 0) * (smartPct[i] || 0) / 100);
        let actualU = null;
        if (fedChannels && Object.prototype.hasOwnProperty.call(fedChannels, chName)) {
          actualU = Math.round(Number(fedChannels[chName]) || 0);
        } else if (Number.isFinite(fedTotal) && fedTotal > 0) {
          // Total fed but no per-channel breakdown — distribute using smart split.
          actualU = Math.round(fedTotal * (smartPct[i] || 0) / 100);
        }
        const gap = actualU !== null ? actualU - smartU : null;
        out.push({ sku: s, brand, channel: chName, smart: smartU, actual: actualU, gap });
      });
    });
    return out;
  }, [skuList, newSkus, actualsRegistry, channelActuals, month, brandFilter, skuFilter, channelFilter]);

  const totals = useMemo(() => {
    let s = 0, a = 0, g = 0, aRows = 0;
    rows.forEach((r) => {
      s += r.smart || 0;
      if (r.actual !== null) { a += r.actual; aRows++; }
      if (r.gap !== null)    { g += r.gap; }
    });
    return { smart: s, actual: aRows ? a : null, gap: aRows ? g : null };
  }, [rows]);

  const showSmart  = view === 'all' || view === 'smart';
  const showActual = view === 'all' || view === 'actual';
  const showGap    = view === 'all' || view === 'gap';

  const download = () => {
    const header = ['SKU', 'Brand', 'Channel'];
    if (showSmart)  header.push('Smart Units');
    if (showActual) header.push('Actual Units');
    if (showGap)    header.push('Gap');
    const wsRows = [header];
    rows.forEach((r) => {
      const row = [r.sku, r.brand, r.channel];
      if (showSmart)  row.push(r.smart);
      if (showActual) row.push(r.actual !== null ? r.actual : '');
      if (showGap)    row.push(r.gap !== null ? r.gap : '');
      wsRows.push(row);
    });
    const totalRow = ['TOTAL', '', ''];
    if (showSmart)  totalRow.push(totals.smart);
    if (showActual) totalRow.push(totals.actual !== null ? totals.actual : '');
    if (showGap)    totalRow.push(totals.gap !== null ? totals.gap : '');
    wsRows.push(totalRow);
    const ws = XLSX.utils.aoa_to_sheet(wsRows);
    ws['!cols'] = [{ wch: 50 }, { wch: 14 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Comparison ${month.replace("'", '_')}`);
    XLSX.writeFile(wb, `RADAR_SmartVsActual_${month.replace("'", '')}.xlsx`);
    toast.success(`Downloaded ${rows.length} rows`);
  };

  return (
    <Card>
      <CardHeader className="py-3 bg-wv-ink text-white rounded-t-md flex flex-row items-center justify-between">
        <p className="text-sm font-semibold">Monthly SKU × Channel — Smart vs Actual vs Gap</p>
        <Button size="sm" variant="secondary" onClick={download} data-testid="cmp-download">
          <Download size={14} className="mr-1.5" /> Download Excel
        </Button>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {/* Month + view + filters */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-3 items-start">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="cmp-month"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {monthOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">View</Label>
            <div className="flex flex-wrap items-center gap-1" data-testid="cmp-view">
              {['smart', 'actual', 'gap', 'all'].map((v) => (
                <Button key={v} size="sm"
                  variant={view === v ? 'default' : 'outline'}
                  onClick={() => setView(v)}
                  data-testid={`cmp-view-${v}`}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </Button>
              ))}
            </div>
          </div>
          <MultiFilter label="Brand" options={brandOptions} selected={brandFilter} onChange={setBrandFilter} testid="cmp-brand" />
          <MultiFilter label="SKU"   options={skuList}      selected={skuFilter}   onChange={setSkuFilter}   testid="cmp-sku" />
          <MultiFilter label="Channel" options={channelNames} selected={channelFilter} onChange={setChannelFilter} testid="cmp-channel" />
        </div>

        <p className="text-[11px] text-muted-foreground">
          {rows.length.toLocaleString()} rows visible · Smart uses engine outputs × SKU-wise channel split · Actual pulled from fed channel breakdowns (Data Feeding Center)
        </p>

        {/* Table */}
        <div className="border border-border rounded-md overflow-auto max-h-[560px]">
          <table className="w-full text-xs" data-testid="cmp-table">
            <thead className="bg-slate-800 text-white sticky top-0 z-10">
              <tr>
                <th className="text-left px-2 py-2">SKU</th>
                <th className="text-left px-2 py-2">Brand</th>
                <th className="text-left px-2 py-2">Channel</th>
                {showSmart  && <th className="text-right px-2 py-2">Smart Units</th>}
                {showActual && <th className="text-right px-2 py-2">Actual Units</th>}
                {showGap    && <th className="text-right px-2 py-2">Gap (A − S)</th>}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 3000).map((r, i) => (
                <tr key={`${r.sku}|${r.channel}|${i}`} className="border-t border-border hover:bg-muted/30">
                  <td className="px-2 py-1 truncate max-w-[420px]">{r.sku}</td>
                  <td className="px-2 py-1 capitalize">{r.brand}</td>
                  <td className="px-2 py-1">{r.channel}</td>
                  {showSmart  && <td className="px-2 py-1 text-right tabular-nums">{fmt(r.smart)}</td>}
                  {showActual && <td className="px-2 py-1 text-right tabular-nums">{r.actual !== null ? fmt(r.actual) : '—'}</td>}
                  {showGap    && (
                    <td className={`px-2 py-1 text-right tabular-nums ${r.gap === null ? '' : (r.gap >= 0 ? 'text-green-700' : 'text-red-600')}`}>
                      {r.gap !== null ? `${r.gap >= 0 ? '+' : ''}${fmt(r.gap)}` : '—'}
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">No rows match current filters.</td></tr>
              )}
              {rows.length > 3000 && (
                <tr><td colSpan={6} className="text-center py-3 text-amber-700 bg-amber-50">Rendered first 3,000 rows for performance — download Excel for full data ({rows.length.toLocaleString()} total).</td></tr>
              )}
            </tbody>
            <tfoot className="bg-muted/40 font-bold sticky bottom-0">
              <tr className="border-t border-border">
                <td colSpan={3} className="px-2 py-2">TOTAL — {rows.length.toLocaleString()} rows</td>
                {showSmart  && <td className="px-2 py-2 text-right tabular-nums">{fmt(totals.smart)}</td>}
                {showActual && <td className="px-2 py-2 text-right tabular-nums">{totals.actual !== null ? fmt(totals.actual) : '—'}</td>}
                {showGap    && (
                  <td className={`px-2 py-2 text-right tabular-nums ${totals.gap === null ? '' : (totals.gap >= 0 ? 'text-green-700' : 'text-red-600')}`}>
                    {totals.gap !== null ? `${totals.gap >= 0 ? '+' : ''}${fmt(totals.gap)}` : '—'}
                  </td>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------- Compact multi-select filter chip ----------------------------- */
function MultiFilter({ label, options, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className="relative">
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full text-left text-xs px-2 py-1.5 border border-input rounded bg-background hover:border-primary/40"
        data-testid={`${testid}-toggle`}>
        {selected.length === 0 ? `All ${label.toLowerCase()}s` : `${selected.length} selected`}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-md border border-border bg-background shadow-lg p-2 space-y-1"
          data-testid={`${testid}-panel`}>
          <div className="flex justify-between border-b border-border pb-1 mb-1">
            <button type="button" onClick={() => onChange([])} className="text-[11px] text-primary hover:underline">Clear</button>
            <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-muted-foreground">Close</button>
          </div>
          {options.map((o) => (
            <label key={o} className="flex items-center gap-2 text-xs px-1 hover:bg-muted rounded cursor-pointer">
              <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
              <span className="truncate capitalize">{o}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Predictive Forecast card --------------------------------------
 * Shown only when the currently-selected SKU is sparse/new. Transparent
 * breakdown of the forecast so ops understand the number: reference SKUs,
 * peer trend, seasonality, category assumption, and own-history weight.
 * As ownWeight → 1, the number naturally converges to the SKU's own
 * Smart Projection — no explicit hand-off logic needed.
 */
function PredictiveForecastCard({ sku, target, forecast }) {
  const { units, trendPct, ownWeight, peerAvg, seasonality, categoryLift,
          references, notes, usable, category, weight } = forecast;
  const trendUp = trendPct >= 0;
  return (
    <Card data-testid="predictive-forecast">
      <CardHeader className="py-3 bg-wv-ink text-white rounded-t-md flex flex-row items-center justify-between">
        <p className="text-sm font-semibold flex items-center gap-2">
          <Sparkles size={14} className="text-primary" /> Predictive Forecast — new / sparse SKU
        </p>
        <Badge variant="secondary" className="text-[10px]">
          {(ownWeight * 100).toFixed(0)}% own · {((1 - ownWeight) * 100).toFixed(0)}% peer
        </Badge>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded border border-primary/30 bg-primary/10 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Projected Units — {target}</p>
            <p className="text-2xl font-bold text-primary tabular-nums" data-testid="predictive-units">{fmt(units)}</p>
          </div>
          <div className="rounded border border-border px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Peer 3M Trend</p>
            <p className={`text-lg font-bold tabular-nums ${trendUp ? 'text-green-700' : 'text-red-600'}`}>
              {trendUp ? '▲' : '▼'} {trendPct.toFixed(1)}%
            </p>
          </div>
          <div className="rounded border border-border px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Seasonality Index</p>
            <p className="text-lg font-bold tabular-nums">{seasonality.toFixed(2)}×</p>
          </div>
          <div className="rounded border border-border px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Category Lift</p>
            <p className="text-lg font-bold tabular-nums">{((categoryLift - 1) * 100).toFixed(1)}%</p>
          </div>
        </div>

        {!usable && (
          <div className="text-xs px-3 py-2 rounded border border-red-200 bg-red-50 text-red-700">
            Not enough reference SKUs to forecast. {notes.join(' ')}
          </div>
        )}

        {usable && (
          <>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Reference SKUs (top-5, weighted by recency)</p>
              <div className="border border-border rounded max-h-56 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-800 text-white sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Peer SKU</th>
                      <th className="text-right px-2 py-1.5">Weight</th>
                      <th className="text-right px-2 py-1.5">Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {references.map((r) => (
                      <tr key={r.sku} className="border-t border-border">
                        <td className="px-2 py-1 truncate max-w-[400px]">{r.sku}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{(r.weight * 100).toFixed(1)}%</td>
                        <td className="px-2 py-1 text-right tabular-nums font-semibold">{fmt(r.contribution)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-0.5">
              <p><b>Blend</b>: baseline = peer 3-month average ({fmt(peerAvg)}) × (1 + peer trend) × seasonality × category lift, then blended with own SKU Smart at {(ownWeight * 100).toFixed(0)}%.</p>
              <p><b>Category</b>: {category || 'unknown'}{Number.isFinite(weight) ? ` · ${weight}g band (±50g peers)` : ''}. Assumption editable in engine (CATEGORY_GROWTH_YOY) or per-SKU via newSkus[sku].categoryGrowth.</p>
              {notes.map((n, i) => <p key={i}>• {n}</p>)}
              <p className="italic">As <b>{sku}</b> accumulates positive-sales months, its own weight rises linearly to 100% at 6 months — the forecast converges to the standard Smart Projection.</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
